import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import { InputWork, InputWorkTitle } from '../../../shared/component/headless/input/InputBody';
import { DimensionsType, DisplayAlignment, DisplaySize, ImageStyle, PEInfoType, TextAlignment, usesWorkDuration } from '../../../shared/enum/EnumRepository';
import { convertWorkSizeToString, PEWorkData, SizeData } from '../../../shared/dto/EntityRepository';
import { useValidation } from '../../../shared/hooks/useValidation';
import SizeInputForPE from '../SizeInputForPE';
import { VariableSizeLabel } from '../WorkInfo';
import MoleculeInputDiv from '../../../shared/component/molecule/MoleculeInputDiv';
import MoleculeTextAreaDiv from '../../../shared/component/molecule/MoleculeTextAreaDiv';
import { InputWorkTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import { useSettingStore } from '../../../shared/store/settingStore';
import { resolveWorkArtistName, shouldShowWorkArtist } from '../../../shared/util/workArtist';
import WorkViewCaption from '../WorkViewCaption';



export interface WorkInfoProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  imageStyle: ImageStyle;
  infoType: PEInfoType;
  data: PEWorkData;
}

/**
 * LANDSCAPE 이미지가 WIDE 일 경우 무조건 보인다.
 * 이미지가 중앙 정렬일 경우 보인다. (CENTER)  
 */
export const isCenterVisible = (
  $displayAlignment: DisplayAlignment,
  $displaySize: DisplaySize,
  $imageStyle: ImageStyle,
  $infoType: PEInfoType = PEInfoType.INLINE,
): boolean => {
  if ($infoType === PEInfoType.NONE) return false;
  if ($displaySize === DisplaySize.WIDE && $imageStyle === ImageStyle.LANDSCAPE) { //보이긴 보이니까 여기서 문제는 아니다. 그런데 center와 center 아닐 경우 달라진다.
    return true;
  }
  if ($displayAlignment === DisplayAlignment.CENTER) {
    return true;
  }
  return false;
};

const WorkInfoComp: React.FC<WorkInfoProps> = ({ peId, alignment, displaySize, imageStyle, infoType, data }) => {
  const handleChange = useProjectElementListStore((s) => s.updateWork);
  const { checkType } = useValidation();
  const { isEditMode } = useEditMode();
  const setting = useSettingStore((state) => state.setting);
  const artistName = !isEditMode && shouldShowWorkArtist(setting)
    ? resolveWorkArtistName(data.artist)
    : '';

  const handleChangeWithValidate = (field: keyof PEWorkData, value: string | SizeData | DimensionsType) => {
    if (!checkType(field, value)) {
      return;
    };
    handleChange(peId, { [field]: value });
  }

  const centerVisible = useMemo(
    () => isCenterVisible(alignment, displaySize, imageStyle, infoType),
    [alignment, displaySize, imageStyle, infoType]
  );

  const sizeOrDuration =
    usesWorkDuration(data.workType)
      ? (data.duration || "—")
      : convertWorkSizeToString(data.size, data.dimensionsType, data.sizeText);
  const inlineInfo = data.prodYear + ", " + data.material + ", " + sizeOrDuration;

  const captionMaxWidth = displaySize === DisplaySize.WIDE ? '100vw' : '90vw';

  const renderInlineInfo = () => {
    if (isEditMode) {
      return (
        <>
          <MoleculeInputDiv
            value={data.prodYear}
            placeholder={"Year"}
            handleChange={(e) => handleChangeWithValidate("prodYear", e.target.value)}
            inputStyle={InfoWorkInputMobile}
            StyledDiv={Info}
          />
          <MoleculeTextAreaDiv
            value={data.material}
            placeholder={"Material"}
            handleChange={(e) => handleChangeWithValidate("material", e.target.value)}
            textareaStyle={InputWorkTextArea}
            StyledDiv={Info}
            alignment={TextAlignment.CENTER}
          />
          {usesWorkDuration(data.workType) ? (
            <MoleculeInputDiv
              value={data.duration}
              placeholder={"00:00:00 (hh:mm:ss)"}
              handleChange={(e) => handleChangeWithValidate("duration", e.target.value)}
              inputStyle={InfoWorkInputMobile}
              StyledDiv={Info}
            />
          ) : (
            <>
              {data.dimensionsType === DimensionsType.FIXED && (
                <SizeInputForPE
                  value={data.size}
                  onChange={(next) => handleChangeWithValidate("size", next)}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.CUSTOM && (
                <MoleculeInputDiv
                  value={data.sizeText ?? ''}
                  placeholder={"Custom size"}
                  handleChange={(e) => handleChangeWithValidate("sizeText", e.target.value)}
                  inputStyle={InfoWorkInputMobile}
                  StyledDiv={Info}
                />
              )}
              {data.dimensionsType === DimensionsType.VARIABLE && (
                <VariableSizeLabel>Dimensions variable</VariableSizeLabel>
              )}
            </>
          )}
        </>
      )
    } else {
      return (
        <>
          {inlineInfo}
        </>
      )
    }
  }

  if (infoType === PEInfoType.NONE) return null;

  return (
    <ImgContentInfoWrapper
      $centerVisible={centerVisible}>
      <WorkViewCaption
        artistName={artistName}
        title={data.title}
        prodYear={data.prodYear}
        material={data.material}
        sizeOrDuration={sizeOrDuration}
        maxWidth={captionMaxWidth}
        centerVisible={centerVisible}
        $marginBottom="10px"
      />
      {isEditMode &&
        <TitleInfoWrapperBottom $centerVisible={centerVisible}>
          <MoleculeInputDiv
            value={data.title}
            placeholder={""}
            handleChange={(e) => handleChangeWithValidate("title", e.target.value)}
            inputStyle={InputWorkTitle}
            StyledDiv={TitleMobile}
          />
          <WorkInfo $isEditMode={isEditMode}>
            {renderInlineInfo()}
          </WorkInfo>
        </TitleInfoWrapperBottom>
      }
    </ImgContentInfoWrapper>
  );
};


const ImgContentInfoWrapper = styled.div<{ $centerVisible: boolean }>`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: ${({ $centerVisible }) => $centerVisible ? 1 : 0};
  transform: ${({ $centerVisible }) => $centerVisible ? 'translateY(0vh)' : 'translateY(-15vh)'};
  z-index: ${({ $centerVisible }) => $centerVisible ? 1 : -10};
  pointer-events: ${({ $centerVisible }) => $centerVisible ? 'auto' : 'none'};
  margin-top: ${({ $centerVisible }) => $centerVisible ? '0.6vh' : '0vh'};
  transition: transform 0.6s ease, opacity 0.5s ease;
`;

/**
 * wide + LANDSCAPE이면 보인다.
 * 중앙정렬이면 보인다.
 */
const TitleInfoWrapperBottom = styled.div<{ $centerVisible: boolean }>`
  position: relative;
  width: 90%;
  height: ${({ $centerVisible }) => $centerVisible ? 'fit-content' : '0vh'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  // overflow: hidden;
  transition: height 0.6s ease;
  margin-bottom: 20px;
`;

const WorkInfo = styled.div<{ $isEditMode: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: ${({ $isEditMode }) => $isEditMode ? 'column' : 'row'};
  justify-content: center;
  align-items: center;
  gap: 1px;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const InfoWorkInputMobile = styled(InputWork)`
  width: 100%;
`;

const Info = styled.div`
  height: 18px;
  text-align: center;
  color: ${({ theme }) => theme.colors.color_Gray_04};
`;

const TitleMobile = styled.h2`
  width: 100%;
  padding: 0px 8px;
  margin-bottom: 0px;
  color: ${({ theme }) => theme.colors.color_Gray_04};
  text-align: center;
  font-style: oblique;
  ${({ theme }) => theme.typography.Body_02_2};
`;

export default WorkInfoComp;
